/**
 * localStorage override (interception layer)
 * ------------------------------------------
 * Goal: route EVERY localStorage access (direct `localStorage.getItem(...)`
 * calls across the app AND the `LS_SERVICE` wrapper) through a single place,
 * WITHOUT changing any existing call sites.
 *
 * This is Step 1: a transparent pass-through. It installs itself over the
 * native localStorage methods and currently behaves exactly like the native
 * localStorage. The whitelist (shared-across-tabs keys) and per-tab tab-id
 * namespacing will be layered into the `resolveKey` / method bodies in later
 * steps, so the flow and call sites never have to change.
 *
 * IMPORTANT: this must be imported/installed at the very top of the app entry
 * (index.js) BEFORE any module that reads or writes localStorage.
 */

import { SHARED_LOCALSTORAGE_KEYS } from "./constant";
import { getTabId } from "./tabId";

// Fast lookup for shared (non-namespaced) keys.
const SHARED_KEYS = new Set(SHARED_LOCALSTORAGE_KEYS);

// Reserved internal keys that must never be namespaced (used by the tab-id
// registry itself). These are always plain.
const RESERVED_KEYS = new Set(["__tabRegistry"]);

// Keep a handle to the real implementation so we always have a way to reach
// the underlying storage, and to avoid infinite recursion once we wrap.
const nativeLocalStorage = window.localStorage;

// Bind the native methods up-front so wrappers can delegate to them safely.
const native = {
    getItem: nativeLocalStorage.getItem.bind(nativeLocalStorage),
    setItem: nativeLocalStorage.setItem.bind(nativeLocalStorage),
    removeItem: nativeLocalStorage.removeItem.bind(nativeLocalStorage),
    clear: nativeLocalStorage.clear.bind(nativeLocalStorage),
    key: nativeLocalStorage.key.bind(nativeLocalStorage),
};

/**
 * Resolve a logical key (what app code uses) to the physical key actually
 * stored in localStorage.
 *
 * - Keys in SHARED_LOCALSTORAGE_KEYS (and reserved internal keys) are returned
 *   as-is, so they are shared across all tabs.
 * - Every other key is prefixed with this tab's id (`${tabId}:key`), so each
 *   tab gets its own isolated copy.
 */
function resolveKey(key) {
    if (SHARED_KEYS.has(key) || RESERVED_KEYS.has(key)) {
        return key;
    }
    const tabId = getTabId();
    // If for some reason the tab id is unavailable, fall back to the plain key
    // rather than losing data under an "undefined:" prefix.
    return tabId ? `${tabId}:${key}` : key;
}

/**
 * The intercepting implementation. Every method delegates to the native
 * storage after passing the key through `resolveKey`. In Step 1 this is a
 * faithful pass-through.
 */
const overriddenMethods = {
    getItem(key) {
        // console.log("[localStorage override] getItem:", key);
        return native.getItem(resolveKey(key));
    },

    setItem(key, value) {
        // console.log("[localStorage override] setItem:", key);
        return native.setItem(resolveKey(key), value);
    },

    removeItem(key) {
        // console.log("[localStorage override] removeItem:", key);
        return native.removeItem(resolveKey(key));
    },

    clear() {
        return native.clear();
    },

    key(index) {
        return native.key(index);
    },
};

let installed = false;

/**
 * Install the override onto the live `window.localStorage` instance.
 *
 * We override the instance methods (not Storage.prototype) so we don't affect
 * sessionStorage, and so delegation to the bound native methods above keeps
 * working. Direct calls like `localStorage.getItem('x')` and `LS_SERVICE`
 * (which internally calls `window.localStorage.getItem`) both go through here.
 */
export function installLocalStorageOverride() {
    if (installed) return;

    try {
        Object.defineProperties(nativeLocalStorage, {
            getItem: {
                value: overriddenMethods.getItem,
                writable: true,
                configurable: true,
            },
            setItem: {
                value: overriddenMethods.setItem,
                writable: true,
                configurable: true,
            },
            removeItem: {
                value: overriddenMethods.removeItem,
                writable: true,
                configurable: true,
            },
            clear: {
                value: overriddenMethods.clear,
                writable: true,
                configurable: true,
            },
            key: {
                value: overriddenMethods.key,
                writable: true,
                configurable: true,
            },
        });
        installed = true;
    } catch (err) {
        // If the environment forbids redefining these (very old browsers),
        // we fail open: the app keeps using native localStorage unchanged.
        // eslint-disable-next-line no-console
        console.error("localStorage override install failed:", err);
    }
}

// Export the native reference and resolveKey for later steps/tests.
export { native as nativeLocalStorageMethods, resolveKey };

// Self-install on import so a bare side-effect import at the very top of the
// entry file guarantees the override is active before any other module runs.
installLocalStorageOverride();

export default installLocalStorageOverride;
